/**
 * Types for Operations Inbox
 * Reuses existing types from app/src/types.tsx where possible
 */

import { TripWithTenant } from '../../types';

/**
 * Urgency levels for trip items based on arrival time
 */
export type UrgencyLevel = 'critical' | 'urgent' | 'soon' | 'normal';

// Urgency thresholds in hours
const CRITICAL_HOURS_THRESHOLD = 4;
const URGENT_HOURS_THRESHOLD = 24;
const SOON_HOURS_THRESHOLD = 48;

/**
 * Get urgency level based on hours until arrival
 * - critical: <4 hours (red)
 * - urgent: <24 hours / today (orange)
 * - soon: <48 hours / tomorrow (yellow)
 * - normal: 2+ days (no badge)
 */
export function getUrgencyLevel(arrivalDate: string | null): UrgencyLevel {
  if (!arrivalDate) return 'normal';
  const hoursUntil = (new Date(arrivalDate).getTime() - Date.now()) / (1000 * 60 * 60);
  if (hoursUntil < 0) return 'critical'; // Already past arrival time
  if (hoursUntil < CRITICAL_HOURS_THRESHOLD) return 'critical';
  if (hoursUntil < URGENT_HOURS_THRESHOLD) return 'urgent';
  if (hoursUntil < SOON_HOURS_THRESHOLD) return 'soon';
  return 'normal';
}

export const URGENCY_COLORS: Record<UrgencyLevel, string | undefined> = {
  critical: '#dc2626', // red-600
  urgent: '#ea580c',   // orange-600
  soon: '#ca8a04',     // yellow-600
  normal: undefined,
};

export const URGENCY_LABELS: Record<UrgencyLevel, string> = {
  critical: 'NOW',
  urgent: 'TODAY',
  soon: 'TOMORROW',
  normal: '',
};

/**
 * Unified inbox item from operations_inbox_v view
 * Can represent either a quarantine item (unknown aircraft) or unassigned trip
 */
export type InboxItem = {
  // Identity
  item_type: 'quarantine' | 'unassigned_trip';
  item_id: string;
  fbo_id: string;

  // Common display fields
  tail_number: string;
  operator_name: string | null;
  aircraft_type_hint: string | null;
  item_source: string;
  created_at: string;

  // Trip data (null for quarantine items)
  trip_id: string | null;
  arrival_date: string | null;
  departure_date: string | null;
  trip_status: string | null;

  // Aircraft data (null for quarantine items)
  aircraft_id: string | null;
  aircraft_make: string | null;
  aircraft_model: string | null;
  wingspan: number | null;
  aircraft_length: number | null;

  // Location data
  hangar_id: string | null;
  ramp_id: string | null;
  location_name: string | null;
  location_type: string;

  // Snooze state
  inbox_state_id: string | null;
  snoozed_until: string | null;
  snoozed_by: string | null;
  snoozed_at: string | null;

  // Computed
  days_until_arrival: number | null;
};

/**
 * Grouped inbox items by action type
 */
export type GroupedInboxItems = {
  unknownAircraft: InboxItem[];
  needsRouting: InboxItem[];
  snoozed: InboxItem[];
};

/**
 * Time-based filter options
 */
export type TimeFilter = 'today' | 'tomorrow' | 'week' | 'all';

/**
 * Filter options for inbox
 */
export type InboxFilters = {
  search: string;
  showSnoozed: boolean;
  timeFilter: TimeFilter;
};

/**
 * Check if an item falls within a time filter
 */
export function matchesTimeFilter(item: InboxItem, filter: TimeFilter): boolean {
  // If no arrival date (quarantine items without dates), show only in 'all' view
  if (!item.arrival_date) return filter === 'all';

  const arrival = new Date(item.arrival_date);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  const startOfDayAfterTomorrow = new Date(startOfTomorrow);
  startOfDayAfterTomorrow.setDate(startOfDayAfterTomorrow.getDate() + 1);
  const startOfNextWeek = new Date(startOfToday);
  startOfNextWeek.setDate(startOfNextWeek.getDate() + 7);

  switch (filter) {
    case 'today':
      return arrival >= startOfToday && arrival < startOfTomorrow;
    case 'tomorrow':
      return arrival >= startOfTomorrow && arrival < startOfDayAfterTomorrow;
    case 'week':
      return arrival >= startOfToday && arrival < startOfNextWeek;
    case 'all':
      return true;
    default:
      return true;
  }
}

/**
 * Snooze options
 */
export type SnoozeOption = {
  label: string;
  getDate: () => Date;
};

export const SNOOZE_OPTIONS: SnoozeOption[] = [
  {
    label: 'Until tomorrow 8am',
    getDate: () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(8, 0, 0, 0);
      return tomorrow;
    },
  },
  {
    label: 'Until Monday 8am',
    getDate: () => {
      const today = new Date();
      const daysUntilMonday = (8 - today.getDay()) % 7 || 7;
      const monday = new Date();
      monday.setDate(today.getDate() + daysUntilMonday);
      monday.setHours(8, 0, 0, 0);
      return monday;
    },
  },
  {
    label: 'For 1 hour',
    getDate: () => {
      const oneHour = new Date();
      oneHour.setHours(oneHour.getHours() + 1);
      return oneHour;
    },
  },
];
